// Renders the server's AI agents inside one Phaser scene. Spawns a walking-sprite
// body per agent (reusing the player's Kenney sheet + 4-direction mapping), drives
// them with gridEngine.moveTo (client-side A*), shows their spoken lines as floating
// bubbles, and keeps the tile→agent registry GameScene uses for taps fresh.

import type GameScene from '../Scenes/GameScene'
import { createCharacterSprite } from '../Scenes/components/NpcAndObjects'
import GlobalInfo from '../GlobalInfo'
import ServerLink, { facingToDir } from './ServerLink'
import { loadAppearancePortrait } from '../avatars/dicebear'
import { buildRecoloredBody, BODY_WALK } from '../avatars/recolor'
import { floatFeedback, SFX_ACTION } from '../Scenes/feedback'
import { MOOD_META } from '../sim/moods'
import { defaultAppearanceFor, type AgentPublicState, type LocationId, type Tile, type Facing } from './protocol'

const REMOTE_SCALE = 1.3

// How a snapshot/state update places an agent: 'place' = drop at its tile (initial snapshot / refresh),
// 'arrival' = spawn at a door and walk in (mid-session entrant), 'reconcile' = only snap a real desync.
type EnsureMode = 'place' | 'arrival' | 'reconcile'

export class RemoteAgentDriver {
    private ids = new Set<string>()
    private sprites = new Map<string, Phaser.Physics.Arcade.Sprite>()
    private labels = new Map<string, Phaser.GameObjects.Text>() // nameplates
    private sigs = new Map<string, string>()                     // appearance signature, to refresh on change
    private handlers: Array<{ ev: string; fn: (...a: any[]) => void }> = []
    private unsubStep?: () => void
    private onUpdate = () => this.repositionLabels()
    // True for the FIRST snapshot after (re)entering this scene: agents are placed directly (they're
    // "already here" when Caz arrives). Cleared after, so live deltas walk arrivals in / departures out.
    // Re-armed on every scene WAKE so revisiting a room doesn't make seated agents walk in from a door.
    private freshEntry = true
    // Agents currently walking OUT to a door before removal (so they don't pop out of existence).
    private leaving = new Set<string>()
    private onWake = () => { this.freshEntry = true }

    constructor(private scene: GameScene, private location: LocationId) {
        this.bind('server-snapshot', (p: any) => { if (p.location === this.location) this.applySnapshot(p.agents) })
        this.bind('server-agent-move', (p: any) => { if (p.location === this.location) this.move(p.agentId, p.target) })
        this.bind('server-agent-teleport', (p: any) => { if (p.location === this.location) this.teleport(p.agentId, p.tile, p.facing) })
        this.bind('server-agent-state', (p: any) => { if (p.agent?.location === this.location) this.ensure(p.agent, 'reconcile') })
        this.bind('server-agent-action', (p: any) => {
            if (p.location !== this.location) return
            if (p.kind === 'use' && p.tile) this.propFeedback(p) // same toast + sound the player gets
            if (p.line) this.bubble(p.agentId, p.line)
        })

        const step = this.scene.gridEngine.positionChangeFinished().subscribe((o: any) => {
            if (this.leaving.has(o.charId)) { this.remove(o.charId); return } // reached the door → gone
            if (this.ids.has(o.charId)) this.rebuildRegistry()
        })
        this.unsubStep = () => step.unsubscribe()
        this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate) // keep nameplates on their sprites
        this.scene.events.on(Phaser.Scenes.Events.WAKE, this.onWake)     // re-arm "fresh entry" on revisit

        this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy())
        this.scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroy())
    }

    private bind(ev: string, fn: (...a: any[]) => void): void {
        GlobalInfo.on(ev, fn)
        this.handlers.push({ ev, fn })
    }

    private applySnapshot(agents: AgentPublicState[]): void {
        const present = new Set(agents.map(a => a.id))
        const fresh = this.freshEntry // the resync snapshot for a (re)entered room: place/remove directly
        this.freshEntry = false
        // Gone from this location: on a fresh entry just drop them; on a live delta walk them out a door.
        for (const id of [...this.ids]) if (!present.has(id)) { if (fresh) this.remove(id); else this.depart(id) }
        for (const a of agents) {
            // A snapshot re-listing an agent that was walking out → cancel the exit, send it back to its tile.
            if (this.leaving.has(a.id)) { this.leaving.delete(a.id); this.move(a.id, a.tile) }
            // New agent → place directly on a fresh entry, else walk it in through a door.
            // Already-present agent → only reconcile a real desync (never re-snap mid-stroll).
            const mode: EnsureMode = this.ids.has(a.id) ? 'reconcile' : (fresh ? 'place' : 'arrival')
            this.ensure(a, mode)
        }
        this.rebuildRegistry()
    }

    private ensure(a: AgentPublicState, mode: EnsureMode = 'place'): void {
        const ge: any = this.scene.gridEngine
        if (!this.ids.has(a.id)) {
            // Recoloured Kenney walking body (real 4-direction walk, per-part skin/hair/shirt).
            const ap = a.appearance ?? defaultAppearanceFor(a.id)
            const key = buildRecoloredBody(this.scene, a.id, ap)
            this.sigs.set(a.id, JSON.stringify(ap))
            loadAppearancePortrait(this.scene, a.id, ap) // avataaars portrait, preloaded for the card
            const sprite = createCharacterSprite(this.scene, 0, 0, key, REMOTE_SCALE)
            sprite.setFrame(BODY_WALK.down.standing)
            // An arrival spawns at the nearest door and WALKS to its spot; everything else is placed
            // directly at its tile (initial snapshot / state refresh).
            const start = mode === 'arrival' ? this.entryNear(a.tile) : a.tile
            this.scene.gridEngine.addCharacter({
                id: a.id,
                sprite,
                startPosition: { x: start.x, y: start.y },
                walkingAnimationMapping: BODY_WALK as any,
                facingDirection: facingToDir(a.facing),
            })
            this.ids.add(a.id)
            this.sprites.set(a.id, sprite)
            if (mode === 'arrival' && (start.x !== a.tile.x || start.y !== a.tile.y)) {
                try { ge.moveTo(a.id, { x: a.tile.x, y: a.tile.y }) } catch { /* unreachable → next snapshot reconciles */ }
            }
        } else if (mode === 'reconcile' || mode === 'place') {
            // Only hard-place on a genuine desync (drifted >2 tiles and not mid-walk). Re-snapping every
            // membershipChanged snapshot is what interrupted the free-time strolls and looked like teleporting.
            if (!ge.isMoving(a.id)) {
                let pos: { x: number; y: number } | undefined
                try { pos = ge.getPosition(a.id) } catch { pos = undefined }
                const drift = pos ? Math.abs(pos.x - a.tile.x) + Math.abs(pos.y - a.tile.y) : 99
                if (drift > 2) ge.setPosition(a.id, { x: a.tile.x, y: a.tile.y })
            }
        }
        this.applyLook(a)
    }

    // The door tile (or scene spawn) nearest an arriving agent's destination — both are walkable, so
    // the agent appears at the doorway and pathfinds in via gridEngine.moveTo.
    private entryNear(target: Tile): Tile {
        const cfg: any = (this.scene as any).cfg
        const doors: Array<{ x: number; y: number }> = cfg?.doors ?? []
        if (doors.length) {
            let best = doors[0], bestD = Infinity
            for (const d of doors) {
                const dist = Math.abs(d.x - target.x) + Math.abs(d.y - target.y)
                if (dist < bestD) { bestD = dist; best = d }
            }
            return { x: best.x, y: best.y }
        }
        return cfg?.spawn ? { x: cfg.spawn.x, y: cfg.spawn.y } : target
    }

    // An agent left this location → walk it OUT through the nearest door, then remove (no pop-out).
    // A fallback timer guarantees removal if the path is blocked, so a leaver can't linger as a ghost.
    private depart(id: string): void {
        if (!this.ids.has(id) || this.leaving.has(id)) return
        const ge: any = this.scene.gridEngine
        const cfg: any = (this.scene as any).cfg
        const doors: Array<{ x: number; y: number }> = cfg?.doors ?? []
        let pos: { x: number; y: number } | undefined
        try { pos = ge.getPosition(id) } catch { pos = undefined }
        if (!doors.length || !pos) { this.remove(id); return } // nowhere to walk out (e.g. off-site) → just go
        let best = doors[0], bestD = Infinity
        for (const d of doors) { const dist = Math.abs(d.x - pos.x) + Math.abs(d.y - pos.y); if (dist < bestD) { bestD = dist; best = d } }
        if (bestD === 0) { this.remove(id); return } // already on the door
        this.leaving.add(id)
        try { ge.moveTo(id, { x: best.x, y: best.y }) } catch { this.remove(id); return }
        this.scene.time.delayedCall(2500, () => { if (this.leaving.has(id)) this.remove(id) })
    }

    // Re-skin on appearance change (admin edited the profile) + manage the nameplate.
    private applyLook(a: AgentPublicState): void {
        const ap = a.appearance ?? defaultAppearanceFor(a.id)
        const sig = JSON.stringify(ap)
        if (this.sigs.get(a.id) && this.sigs.get(a.id) !== sig) {
            this.sigs.set(a.id, sig)
            const key = buildRecoloredBody(this.scene, a.id, ap)
            this.sprites.get(a.id)?.setTexture(key)
            loadAppearancePortrait(this.scene, a.id, ap)
        }
        // Nameplate only for admin-named avatars (real first name) — avoids 28 "Élève" clones cluttering/clipping.
        if (a.named) this.ensureLabel(a.id, a.displayName.split(' ')[0])
        else { this.labels.get(a.id)?.destroy(); this.labels.delete(a.id) }
    }

    private ensureLabel(id: string, name: string): void {
        let t = this.labels.get(id)
        if (!t) {
            t = this.scene.add.text(0, -999, name, {
                fontFamily: 'sans-serif', fontSize: '9px', color: '#ffffff',
                backgroundColor: '#10151fcc', padding: { x: 3, y: 1 }, align: 'center',
            }).setOrigin(0.5, 1).setDepth(960).setResolution(3)
            this.labels.set(id, t)
        } else if (t.text !== name) {
            t.setText(name)
        }
    }

    private repositionLabels(): void {
        const z = this.scene.cameras.main.zoom || 1
        for (const [id, label] of this.labels) {
            const sprite = this.sprites.get(id)
            if (!sprite) continue
            const b = sprite.getBounds() // visual rect (origin/scale-proof) → sit just above the head
            label.setPosition(b.centerX, b.top - 2).setScale(1 / z)
        }
    }

    private move(id: string, target: Tile): void {
        if (!this.ids.has(id)) return
        const ge: any = this.scene.gridEngine
        try { ge.moveTo(id, { x: target.x, y: target.y }) } catch { /* unreachable → server reconciles */ }
    }

    private teleport(id: string, tile: Tile, _facing?: Facing): void {
        const a = ServerLink.getAgent(id)
        if (a) {
            this.ensure(a)
            ;(this.scene.gridEngine as any).setPosition(id, { x: tile.x, y: tile.y })
            this.rebuildRegistry() // setPosition is instant (no positionChangeFinished) → refresh tap tiles
        }
    }

    private remove(id: string): void {
        this.leaving.delete(id)
        if (!this.ids.has(id)) return
        try { (this.scene.gridEngine as any).removeCharacter(id) } catch { /* */ }
        this.sprites.get(id)?.destroy()
        this.sprites.delete(id)
        this.labels.get(id)?.destroy()
        this.labels.delete(id)
        this.sigs.delete(id)
        this.ids.delete(id)
        this.rebuildRegistry() // an agent left → drop its stale tap tile so an empty desk can't open a Kazi card
    }

    // An agent used a prop → show the SAME floating toast + sound the human player gets.
    private propFeedback(p: any): void {
        const wx = p.tile.x * 16 + 8, wy = p.tile.y * 16
        const parts = Object.keys(p.effects || {}).map((k) => {
            const d = p.effects[k]; const meta = MOOD_META.find(m => m.key === k)
            return (d >= 0 ? '+' : '') + d + ' ' + (meta ? meta.label : k)
        })
        if (this.scene.cache.audio.exists(SFX_ACTION)) this.scene.sound.play(SFX_ACTION, { volume: 0.35 })
        floatFeedback(this.scene, wx, wy, `${p.label}\n${parts.join('  ')}`)
    }

    // Floating speech bubble above an agent.
    private bubble(id: string, line: string): void {
        const sprite = this.sprites.get(id)
        if (!sprite) return
        const t = this.scene.add.text(sprite.x, sprite.y - 18, line, {
            fontFamily: 'sans-serif', fontSize: '12px', color: '#10151f',
            backgroundColor: '#ffffffe6', padding: { x: 6, y: 4 }, align: 'center',
            wordWrap: { width: 160 },
        }).setOrigin(0.5, 1).setDepth(970).setResolution(3)
        t.setScale(1 / (this.scene.cameras.main.zoom || 1))
        this.scene.tweens.add({ targets: t, y: t.y - 14, alpha: 0, duration: 2600, ease: 'Cubic.easeOut', onComplete: () => t.destroy() })
    }

    // Rebuild the tile→agent registry GameScene.pointerdown reads for taps.
    private rebuildRegistry(): void {
        const reg: Record<string, { id: string; displayName: string; role: string; dialogueHook: string }> = {}
        const ge: any = this.scene.gridEngine
        for (const id of this.ids) {
            let pos: any
            try { pos = ge.getPosition(id) } catch { continue }
            const a = ServerLink.getAgent(id)
            reg[`${pos.x},${pos.y}`] = {
                id,
                displayName: a?.displayName ?? id,
                role: a?.kind === 'staff' ? 'Personnel' : 'Élève',
                dialogueHook: '',
            }
        }
        ;(this.scene as any).kaziByTile = reg
    }

    destroy(): void {
        this.unsubStep?.()
        this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate)
        this.scene.events.off(Phaser.Scenes.Events.WAKE, this.onWake)
        for (const { ev, fn } of this.handlers) GlobalInfo.off(ev, fn)
        this.handlers = []
        for (const id of [...this.ids]) this.remove(id)
    }
}
